import { serviceCountryName } from './serviceRequestCountries.js';

export const S13_INCIDENT_TYPES = [
    'Security threat or armed incident', 'Fire, explosion, or hazmat', 'Natural disaster or severe weather',
    'Cyber, IT, or data breach', 'Reputational or media crisis', 'Civil unrest or protest',
    'Medical emergency or mass casualty', 'Supply chain or operational failure',
] as const;
export const S13_PRIORITIES = ['P1', 'P2', 'P3', 'P4'] as const;
export const S13_REVIEW_FREQUENCIES = ['Quarterly', 'Every six months', 'Annually'] as const;

export type S13CommandTeam = { incidentDirector: string; operationsLead: string; safetyLead: string;
    communicationsLead: string; siteCoordinators: string };
export type S13PriorityRule = { priority: string; trigger: string; responseMinutes: string; authority: string };
export type S13Function = { name: string; rtoHours: string };
export type S13Intake = {
    organizationName: string;
    industry: string;
    sites: string[];
    siteDetails: string;
    commandTeam: S13CommandTeam;
    priorityRules: S13PriorityRule[];
    externalSpokesperson: string;
    criticalFunctions: S13Function[];
    insuranceNotifications: string;
    supplierContacts: string;
    policeFireContacts: string;
    reviewFrequency: string;
    debriefProtocol: string;
    templateContext?: string;
};

const text = (value: unknown, maximum = 4000) => String(value ?? '').trim().slice(0, maximum);
const strings = (value: unknown) => Array.isArray(value)
    ? [...new Set(value.map(item => text(item, 240)).filter(Boolean))].slice(0, 20) : [];

export function emptyS13Intake(): S13Intake {
    return { organizationName: '', industry: '', sites: [], siteDetails: '',
        commandTeam: { incidentDirector: '', operationsLead: '', safetyLead: '', communicationsLead: '', siteCoordinators: '' },
        priorityRules: S13_PRIORITIES.map(priority => ({ priority, trigger: '', responseMinutes: '', authority: '' })),
        externalSpokesperson: '', criticalFunctions: [], insuranceNotifications: '', supplierContacts: '',
        policeFireContacts: '',
        reviewFrequency: '', debriefProtocol: '' };
}

export function prefillS13Intake(organization: any): S13Intake {
    const address = (value: any) => [value?.addressLine1, value?.addressLine2, value?.city,
        value?.administrativeArea, serviceCountryName(value?.country || value?.countryCode)]
        .map(part => text(part, 160)).filter(Boolean).join(', ');
    return { ...emptyS13Intake(), organizationName: text(organization?.name || organization?.brandName, 240),
        industry: text(organization?.industry, 160), sites: strings([
            address(organization?.headquartersAddress),
            ...(Array.isArray(organization?.additionalLocations) ? organization.additionalLocations.map(address) : []),
        ]) };
}

export function cleanS13Intake(source: any): S13Intake {
    const input = source && typeof source === 'object' ? source : {};
    const team = input.commandTeam || {};
    const rules = Array.isArray(input.priorityRules) ? input.priorityRules : [];
    return {
        organizationName: text(input.organizationName, 240), industry: text(input.industry, 160),
        sites: strings(input.sites), siteDetails: text(input.siteDetails),
        commandTeam: {
            incidentDirector: text(team.incidentDirector, 240), operationsLead: text(team.operationsLead, 240),
            safetyLead: text(team.safetyLead, 240), communicationsLead: text(team.communicationsLead, 240),
            siteCoordinators: text(team.siteCoordinators, 1000),
        },
        priorityRules: S13_PRIORITIES.map(priority => {
            const rule = rules.find((item: any) => item?.priority === priority) || {};
            return { priority, trigger: text(rule.trigger, 1000), responseMinutes: text(rule.responseMinutes, 20),
                authority: text(rule.authority, 240) };
        }),
        externalSpokesperson: text(input.externalSpokesperson, 500),
        criticalFunctions: (Array.isArray(input.criticalFunctions) ? input.criticalFunctions : []).slice(0, 20)
            .map((item: any) => ({ name: text(item?.name, 160), rtoHours: text(item?.rtoHours, 20) })),
        insuranceNotifications: text(input.insuranceNotifications), supplierContacts: text(input.supplierContacts),
        policeFireContacts: text(input.policeFireContacts),
        reviewFrequency: S13_REVIEW_FREQUENCIES.includes(input.reviewFrequency) ? input.reviewFrequency : '',
        debriefProtocol: text(input.debriefProtocol),
        ...(text(input.templateContext, 20000) ? { templateContext: text(input.templateContext, 20000) } : {}),
    };
}

export function missingS13Intake(source: any): string[] {
    const intake = cleanS13Intake(source);
    const missing: string[] = [];
    if (!intake.organizationName || !intake.industry || !intake.sites.length)
        missing.push('Organisation, industry, and sites');
    if (Object.values(intake.commandTeam).some(value => !value))
        missing.push('Named incident command team');
    if (intake.priorityRules.some(rule => !rule.trigger || !rule.authority
        || !Number.isFinite(Number(rule.responseMinutes)) || Number(rule.responseMinutes) <= 0))
        missing.push('P1–P4 triggers, times, and authority');
    if (!intake.externalSpokesperson)
        missing.push('External communication authority');
    if (!intake.criticalFunctions.length || intake.criticalFunctions.some(item => !item.name
        || !Number.isFinite(Number(item.rtoHours)) || Number(item.rtoHours) <= 0)
        || new Set(intake.criticalFunctions.map(item => item.name.toLowerCase())).size !== intake.criticalFunctions.length)
        missing.push('Critical functions and RTOs');
    if (!intake.insuranceNotifications)
        missing.push('Insurance and notification decision');
    if (!intake.supplierContacts)
        missing.push('Supplier and contractor contacts');
    if (!intake.reviewFrequency)
        missing.push('Plan review cycle');
    if (!intake.debriefProtocol)
        missing.push('Post-incident debrief protocol');
    return missing;
}
